// Wire models, ported 1:1 from the Flutter client (client/lib/models). Parsing is
// lenient on purpose: unknown rarities become common, unknown slots are dropped.

export const RARITIES = ['common', 'uncommon', 'rare', 'epic', 'legendary'] as const;
export type Rarity = (typeof RARITIES)[number];

export function parseRarity(value: unknown): Rarity {
  return (RARITIES as readonly string[]).includes(value as string) ? (value as Rarity) : 'common';
}
export const rarityLabel = (r: Rarity): string => r[0].toUpperCase() + r.slice(1);
/** Small -> big, used to scale celebrations. */
export const rarityWeight = (r: Rarity): number => RARITIES.indexOf(r) + 1;

/** Where an item goes on the avatar, in draw order back to front. The wire name is the value. */
export const SLOTS = ['back', 'body', 'feet', 'neck', 'face', 'head', 'hand', 'companion'] as const;
export type Slot = (typeof SLOTS)[number];

export function parseSlot(value: unknown): Slot | null {
  return (SLOTS as readonly string[]).includes(value as string) ? (value as Slot) : null;
}
export const slotLabel = (s: Slot): string => s[0].toUpperCase() + s.slice(1);

export const DEFAULT_AVATAR_BODY = 'basic';
export const KNOWN_AVATAR_BODIES: ReadonlySet<string> = new Set([DEFAULT_AVATAR_BODY]);

export type Equipped = Partial<Record<Slot, string>>;

export interface Avatar {
  body: string;
  equipped: Equipped;
  /** Server write time in epoch ms; orders avatar versions. */
  updatedAt?: number;
}

export const basicAvatar = (): Avatar => ({ body: DEFAULT_AVATAR_BODY, equipped: {} });

type Json = Record<string, unknown>;
const num = (v: unknown, fallback = 0): number => (typeof v === 'number' ? Math.trunc(v) : fallback);

export function parseAvatar(json: unknown): Avatar {
  if (json == null || typeof json !== 'object') return basicAvatar();
  const j = json as Json;
  const equipped: Equipped = {};
  const raw = (j.equipped ?? {}) as Json;
  for (const [key, value] of Object.entries(raw)) {
    const slot = parseSlot(key);
    if (slot && typeof value === 'string') equipped[slot] = value;
  }
  const body = typeof j.body === 'string' && KNOWN_AVATAR_BODIES.has(j.body) ? j.body : DEFAULT_AVATAR_BODY;
  return { body, equipped, updatedAt: typeof j.updatedAt === 'number' ? Math.trunc(j.updatedAt) : undefined };
}

export interface PublicPlayer {
  playerId: string;
  displayName: string;
  score: number;
  huntsCompleted: number;
  avatar: Avatar;
}

export const parsePublicPlayer = (j: Json): PublicPlayer => ({
  playerId: j.playerId as string,
  displayName: j.displayName as string,
  score: num(j.score),
  huntsCompleted: num(j.huntsCompleted),
  avatar: parseAvatar(j.avatar),
});

// ---- Catalog --------------------------------------------------------------------------

export interface CatalogPart {
  id: string;
  name: string;
}

export interface CatalogItem {
  id: string;
  name: string;
  description: string;
  rarity: Rarity;
  emoji: string;
  /** `#rrggbb` */
  palette: string;
  slot: Slot | null;
  parts: CatalogPart[];
}

export const isMultiPart = (item: CatalogItem): boolean => item.parts.length > 0;
export const itemPart = (item: CatalogItem, partId?: string | null): CatalogPart | undefined =>
  partId ? item.parts.find((p) => p.id === partId) : undefined;

export function parseCatalogItem(j: Json): CatalogItem {
  return {
    id: j.id as string,
    name: j.name as string,
    description: j.description as string,
    rarity: parseRarity(j.rarity),
    emoji: j.emoji as string,
    palette: j.palette as string,
    slot: parseSlot(j.slot),
    parts: ((j.parts as Json[] | undefined) ?? []).map((p) => ({ id: p.id as string, name: p.name as string })),
  };
}

/** Placeholder shown when the server references an item this build does not know. */
export const mysteryItem = (id: string): CatalogItem => ({
  id,
  name: 'Mystery Item',
  description: 'Something from the Ethr this version of the app cannot quite see yet. Update to reveal it.',
  rarity: 'common',
  emoji: '❔',
  palette: '#B0B8C8',
  slot: null,
  parts: [],
});

// ---- Player ---------------------------------------------------------------------------

export interface PlayerStats {
  scans: number;
  finds: number;
  pickups: number;
  huntsCompleted: number;
}

export interface PlayerProfile {
  playerId: string;
  displayName: string;
  createdAt: number;
  lastScanAt?: number;
  lastPickupAt?: number;
  stats: PlayerStats;
}

export interface ItemInstance {
  instanceId: string;
  itemId: string;
  rarity: Rarity;
  foundAt: string;
  foundAtTs: number;
  assembled: boolean;
}

export interface PartHint {
  partId: string;
  distanceMeters: number;
  bearingDegrees: number;
}

/** "~800 m" / "~1.5 km" style rounding so the hunt stays a hunt. */
export function roughDistance(h: PartHint): string {
  if (h.distanceMeters < 1000) return `~${Math.round(h.distanceMeters / 50) * 50} m`;
  return `~${Math.round(h.distanceMeters / 100) / 10} km`;
}

export function compass(h: PartHint): string {
  const points = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return points[Math.floor((h.bearingDegrees + 22.5) / 45) % 8];
}

export interface HuntView {
  itemId: string;
  rarity: Rarity;
  startedAt: number;
  collected: string[];
  totalParts: number;
  hints: PartHint[];
}

export interface Player {
  profile: PlayerProfile;
  inventory: ItemInstance[];
  hunts: HuntView[];
  catalogVersion: number;
  avatar: Avatar;
}

const parseStats = (j?: Json | null): PlayerStats => ({
  scans: num(j?.scans),
  finds: num(j?.finds),
  pickups: num(j?.pickups),
  huntsCompleted: num(j?.huntsCompleted),
});

const parseInstance = (j: Json): ItemInstance => ({
  instanceId: j.instanceId as string,
  itemId: j.itemId as string,
  rarity: parseRarity(j.rarity),
  foundAt: j.foundAt as string,
  foundAtTs: num(j.foundAtTs),
  assembled: (j.assembled as boolean | undefined) ?? false,
});

const parseHint = (j: Json): PartHint => ({
  partId: j.partId as string,
  distanceMeters: num(j.distanceMeters),
  bearingDegrees: num(j.bearingDegrees),
});

export const parseHunt = (j: Json): HuntView => ({
  itemId: j.itemId as string,
  rarity: parseRarity(j.rarity),
  startedAt: num(j.startedAt),
  collected: ((j.collected as string[] | undefined) ?? []).slice(),
  totalParts: num(j.totalParts),
  hints: ((j.hints as Json[] | undefined) ?? []).map(parseHint),
});

export function parsePlayer(j: Json): Player {
  const p = j.profile as Json;
  return {
    profile: {
      playerId: p.playerId as string,
      displayName: p.displayName as string,
      createdAt: num(p.createdAt),
      lastScanAt: typeof p.lastScanAt === 'number' ? Math.trunc(p.lastScanAt) : undefined,
      lastPickupAt: typeof p.lastPickupAt === 'number' ? Math.trunc(p.lastPickupAt) : undefined,
      stats: parseStats(p.stats as Json | undefined),
    },
    inventory: ((j.inventory as Json[] | undefined) ?? []).map(parseInstance),
    hunts: ((j.hunts as Json[] | undefined) ?? []).map(parseHunt),
    catalogVersion: num(j.catalogVersion),
    avatar: parseAvatar(j.avatar),
  };
}

// ---- Scan / resolve -------------------------------------------------------------------

export interface FindView {
  findId: string;
  itemId: string;
  rarity: Rarity;
  partId?: string;
  expiresAt: number;
}

export type NothingReason = 'alreadySearched' | 'emptyCell' | 'unknown';

export type ScanResult =
  | { kind: 'found'; find: FindView }
  | { kind: 'nothing'; reason: NothingReason; nextWindowAt: number };

export function parseScanResult(j: Json): ScanResult {
  switch (j.result) {
    case 'found': {
      const f = j.find as Json;
      return {
        kind: 'found',
        find: {
          findId: f.findId as string,
          itemId: f.itemId as string,
          rarity: parseRarity(f.rarity),
          partId: (f.partId as string | null | undefined) ?? undefined,
          expiresAt: num(f.expiresAt),
        },
      };
    }
    case 'nothing':
      return {
        kind: 'nothing',
        reason: j.reason === 'already_searched' ? 'alreadySearched' : j.reason === 'empty_cell' ? 'emptyCell' : 'unknown',
        nextWindowAt: num(j.nextWindowAt),
      };
    default:
      throw new Error(`Unknown scan result: ${String(j.result)}`);
  }
}

export type ResolveResult =
  | { kind: 'picked_up'; item: ItemInstance }
  | { kind: 'part_collected'; hunt: HuntView }
  | { kind: 'left' };

export function parseResolveResult(j: Json): ResolveResult {
  switch (j.outcome) {
    case 'picked_up':
      return { kind: 'picked_up', item: parseInstance(j.item as Json) };
    case 'part_collected':
      return { kind: 'part_collected', hunt: parseHunt(j.hunt as Json) };
    case 'left':
      return { kind: 'left' };
    default:
      throw new Error(`Unknown resolve outcome: ${String(j.outcome)}`);
  }
}

// ---- Leaderboard ----------------------------------------------------------------------

export interface LeaderboardEntry {
  playerId: string;
  displayName: string;
  score: number;
  rank: number;
  isMe: boolean;
}

export const parseEntry = (j: Json): LeaderboardEntry => ({
  playerId: j.playerId as string,
  displayName: j.displayName as string,
  score: num(j.score),
  rank: num(j.rank),
  isMe: (j.isMe as boolean | undefined) ?? false,
});

export interface LeaderboardWindow {
  me: LeaderboardEntry;
  /** Best first; includes `me`. */
  entries: LeaderboardEntry[];
  upCursor: string | null;
  downCursor: string | null;
  totalPlayers: number;
}

export function parseLeaderboardWindow(j: Json): LeaderboardWindow {
  const cursors = (j.cursors as Json | undefined) ?? {};
  return {
    me: parseEntry(j.me as Json),
    entries: ((j.entries as Json[] | undefined) ?? []).map(parseEntry),
    upCursor: (cursors.up as string | null | undefined) ?? null,
    downCursor: (cursors.down as string | null | undefined) ?? null,
    totalPlayers: typeof j.totalPlayers === 'number' ? Math.trunc(j.totalPlayers) : 1,
  };
}

export interface LeaderboardPage {
  entries: LeaderboardEntry[];
  cursor: string | null;
}

export const parseLeaderboardPage = (j: Json): LeaderboardPage => ({
  entries: ((j.entries as Json[] | undefined) ?? []).map(parseEntry),
  cursor: (j.cursor as string | null | undefined) ?? null,
});

export const rankLabel = (rank: number): string => (rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : `#${rank}`);
